import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { ITag, ICreateTagRequest, IUpdateLocationTagsRequest } from '@map-bldr/shared';
import { useLocations } from './locationHooks';
import { useEffect } from 'react';

/**
 * Unified hook for managing location tags with optimistic updates
 * Returns query data and mutation functions for adding, removing, and replacing tags
 */
export function useLocationTags(locationId: string) {
  const queryClient = useQueryClient();

  // Query for location's tag IDs
  const query = useQuery<string[]>({
    queryKey: ['locationTagLinks', locationId],
    queryFn: () => apiClient.get<string[]>(`/locations/${locationId}/tags`),
  });

  // Add tag mutation with optimistic update
  const addTagMutation = useMutation({
    mutationFn: (tagId: string) =>
      apiClient.put<void>(`/locations/${locationId}/tags/${tagId}`),
    onMutate: async (tagId: string) => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['locationTagLinks', locationId] });

      // Snapshot previous value for rollback
      const previousTags = queryClient.getQueryData<string[]>(['locationTagLinks', locationId]);

      // Optimistically update cache
      queryClient.setQueryData<string[]>(['locationTagLinks', locationId], (old = []) => {
        // Don't add if already present
        if (old.includes(tagId)) return old;
        return [...old, tagId];
      });

      return { previousTags };
    },
    onError: (_err, _tagId, context) => {
      // Rollback on error
      if (context?.previousTags) {
        queryClient.setQueryData(['locationTagLinks', locationId], context.previousTags);
      }
    },
    onSettled: () => {
      // Refetch to ensure cache is in sync with server
      queryClient.invalidateQueries({ queryKey: ['locationTagLinks', locationId] });
    },
  });

  // Remove tag mutation with optimistic update
  const removeTagMutation = useMutation({
    mutationFn: (tagId: string) =>
      apiClient.delete<void>(`/locations/${locationId}/tags/${tagId}`),
    onMutate: async (tagId: string) => {
      await queryClient.cancelQueries({ queryKey: ['locationTagLinks', locationId] });
      const previousTags = queryClient.getQueryData<string[]>(['locationTagLinks', locationId]);

      // Optimistically remove from cache
      queryClient.setQueryData<string[]>(['locationTagLinks', locationId], (old = []) =>
        old.filter((id) => id !== tagId)
      );

      return { previousTags };
    },
    onError: (_err, _tagId, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(['locationTagLinks', locationId], context.previousTags);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['locationTagLinks', locationId] });
    },
  });

  // Replace all tags mutation with optimistic update
  const replaceTagsMutation = useMutation({
    mutationFn: (tagIds: string[]) =>
      apiClient.put<void>(`/locations/${locationId}/tags`, {
        tag_ids: tagIds,
      } as IUpdateLocationTagsRequest),
    onMutate: async (tagIds: string[]) => {
      await queryClient.cancelQueries({ queryKey: ['locationTagLinks', locationId] });
      const previousTags = queryClient.getQueryData<string[]>(['locationTagLinks', locationId]);

      // Optimistically replace all tags
      queryClient.setQueryData<string[]>(['locationTagLinks', locationId], tagIds);

      return { previousTags };
    },
    onError: (_err, _tagIds, context) => {
      if (context?.previousTags) {
        queryClient.setQueryData(['locationTagLinks', locationId], context.previousTags);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['locationTagLinks', locationId] });
    },
  });

  return {
    // Query data and states
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,

    // Mutation functions
    addTag: addTagMutation.mutate,
    addTagAsync: addTagMutation.mutateAsync,
    isAddingTag: addTagMutation.isPending,

    removeTag: removeTagMutation.mutate,
    removeTagAsync: removeTagMutation.mutateAsync,
    isRemovingTag: removeTagMutation.isPending,

    replaceTags: replaceTagsMutation.mutate,
    replaceTagsAsync: replaceTagsMutation.mutateAsync,
    isReplacingTags: replaceTagsMutation.isPending,
  };
}

export function usePrefetchLocationTagLinks() {
  const queryClient = useQueryClient();
  const { data: locations = [] } = useLocations();

  useEffect(() => {
    locations.forEach((location) => {
      queryClient.prefetchQuery({
        queryKey: ['locationTagLinks', location.id],
        queryFn: () => apiClient.get<string[]>(`/locations/${location.id}/tags`),
      });
    });
  }, [locations, queryClient]);
}

// Legacy hooks - kept for backward compatibility, can be removed if not needed
export function useLocationTagLinks(locationId: string) {
  return useQuery<string[]>({
    queryKey: ['locationTagLinks', locationId],
    queryFn: () => apiClient.get<string[]>(`/locations/${locationId}/tags`),
  });
}

export interface IAddLocationTagParams {
  locationId: string;
  tagId: string;
}

export function useAddLocationTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, tagId }: IAddLocationTagParams) =>
      apiClient.put<void>(`/locations/${locationId}/tags/${tagId}`),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['locationTagLinks', variables.locationId] });
    },
  });
}

export interface IRemoveLocationTagParams {
  locationId: string;
  tagId: string;
}

export function useRemoveLocationTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, tagId }: IRemoveLocationTagParams) =>
      apiClient.delete<void>(`/locations/${locationId}/tags/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['locationTagLinks'] });
    },
  });
}

export interface IUpdateLocationTagsParams {
  locationId: string;
  tagIds: string[];
}

export function useUpdateLocationTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ locationId, tagIds }: IUpdateLocationTagsParams) => {
      return await apiClient.put<void>(
        `/locations/${locationId}/tags`,
        { tag_ids: tagIds } as IUpdateLocationTagsRequest
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locationTagLinks'] });
    },
  });
}

export interface ICreateAndApplyTagParams {
  locationId: string;
  tag: ICreateTagRequest;
}

export function useCreateAndApplyTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ locationId, tag }: ICreateAndApplyTagParams) => {
      const createdTag = await apiClient.post<ITag>('/tags', tag);

      await apiClient.put<void>(`/locations/${locationId}/tags/${createdTag.id}`);

      return createdTag;
    },
    onMutate: async ({ tag, locationId }: ICreateAndApplyTagParams) => {
      const optimisticTag: ITag = {
        ...tag,
      };
      queryClient.setQueryData<ITag[]>(['tags'], (old = []) => [...old, optimisticTag]);
      queryClient.setQueryData<string[]>(['locationTagLinks', locationId], (old = []) => [...old, optimisticTag.id]);
    },
    onSuccess: (data, params) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['locationTagLinks', params.locationId] });
    },
  });
}
